import { getSettings } from './storage';

const MILES_TO_KM = 1.60934;

/**
 * Get the user's preferred distance unit.
 * Returns 'miles' or 'km'.
 * @param {string} [unitOverride] - Per-vehicle override ('miles' or 'km'). Takes precedence over global setting.
 */
export function getDistanceUnit(unitOverride) {
  if (unitOverride === 'miles' || unitOverride === 'km') return unitOverride;
  const settings = getSettings();
  return settings.preferences?.units === 'km' ? 'km' : 'miles';
}

/**
 * Returns the short label for the preferred unit.
 * 'mi' or 'km'
 * @param {string} [unitOverride] - Per-vehicle override
 */
export function distanceLabel(unitOverride) {
  return getDistanceUnit(unitOverride) === 'km' ? 'km' : 'mi';
}

/**
 * Returns the full label for the preferred unit.
 * 'miles' or 'kilometers'
 * @param {string} [unitOverride] - Per-vehicle override
 */
export function distanceLabelFull(unitOverride) {
  return getDistanceUnit(unitOverride) === 'km' ? 'kilometers' : 'miles';
}

/**
 * Convert a miles value to the display unit.
 * Returns a number (not formatted).
 * @param {number} miles - Value stored in miles
 * @param {string} [unitOverride] - Per-vehicle override
 */
export function milesToDisplay(miles, unitOverride) {
  if (miles == null || isNaN(miles)) return null;
  const num = Number(miles);
  return getDistanceUnit(unitOverride) === 'km' ? Math.round(num * MILES_TO_KM) : num;
}

/**
 * Convert a display-unit value back to miles for storage.
 * @param {number} value - Value in display units
 * @param {string} [unitOverride] - Per-vehicle override
 */
export function displayToMiles(value, unitOverride) {
  if (value == null || isNaN(value)) return null;
  const num = Number(value);
  return getDistanceUnit(unitOverride) === 'km' ? Math.round(num / MILES_TO_KM) : num;
}

/**
 * Format a miles value for display: converts + adds locale formatting + appends unit label.
 * e.g. "45,000 mi" or "72,420 km"
 * @param {number} miles - Value stored in miles
 * @param {string} [unitOverride] - Per-vehicle override
 */
export function formatDistance(miles, unitOverride) {
  const display = milesToDisplay(miles, unitOverride);
  if (display == null) return '';
  return `${parseInt(display).toLocaleString()} ${distanceLabel(unitOverride)}`;
}

/**
 * Format a meters value for route / driving displays.
 * Auto-uses the user's distance pref. Drops to a decimal if under 1 mi/km
 * so short steps like "0.3 mi" or "100 m" read naturally.
 *
 * @param {number} meters
 * @param {string} [unitOverride] - 'miles' / 'km'
 * @returns {string} '5.4 mi' / '8.7 km' / '120 m'
 */
export function formatMeters(meters, unitOverride) {
  if (meters == null || isNaN(meters)) return '';
  const isMetric = getDistanceUnit(unitOverride) === 'km';
  if (isMetric) {
    if (meters < 1000) return `${Math.round(meters)} m`;
    return `${(meters / 1000).toFixed(1)} km`;
  }
  const miles = meters / 1609.34;
  if (miles < 0.1) return `${Math.round(meters)} m`;
  return `${miles.toFixed(1)} mi`;
}
